import {
  Point3D,
  OTHER_COLOR,
  BRIGHT_COLORS,
  groupKeys,
  GroupDataSet,
  groupKeysList,
  DataSet,
  GroupData,
  UNKNOWN_COLOR,
} from "../cosmo-graph/model";

// groupColumnIndex is the column index of groupKeysList
export const processCsvData = (
  csvText: string,
  groupColumnIndex: number
): { points: Point3D[]; groupData: GroupDataSet } => {
  const lines = csvText.split(/\r?\n/);
  if (lines.length < 2) throw new Error("File is empty or invalid");

  const headers = lines[0].split(",").map((h) => h.trim());
  const lowerHeaders = headers.map((h) => h.toLowerCase());
  const rawPoints: Point3D[] = [];

  const groupData: GroupDataSet = {};
  const isValid = (val: string) => {
    const v = val.trim().toLowerCase();
    return v && v !== "nan" && v !== "unknown" && v !== "null" && v !== "";
  };

  groupKeysList.forEach((key) => {
    let index = lowerHeaders.indexOf(groupKeys[key][groupColumnIndex]);
    // if (index === -1 && groupKeys[key][1]) {
    //   index = lowerHeaders.indexOf(groupKeys[key][1]);
    // }
    if (index === -1) {
      throw new Error(
        `CSV must contain column: ${groupKeys[key][0]} or ${groupKeys[key][1]} for ${key}`
      );
    }
    groupData[key] = { dataCount: {}, dataList: [], index };
  });

  const idIndex = lowerHeaders.indexOf("id");
  const xIndex = lowerHeaders.indexOf("x");
  const yIndex = lowerHeaders.indexOf("y");
  const zIndex = lowerHeaders.indexOf("z");
  if (xIndex === -1 || yIndex === -1 || zIndex === -1) {
    throw new Error("CSV must contain columns: x, y, z");
  }

  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].trim().split(",");
    if (parts.length < headers.length) continue;

    const x = parseFloat(parts[xIndex]);
    const y = parseFloat(parts[yIndex]);
    const z = parseFloat(parts[zIndex]);
    if (isNaN(x) || isNaN(y) || isNaN(z)) {
      console.warn(
        `Invalid coordinates at line ${i}: ${parts[xIndex]}, ${parts[yIndex]}, ${parts[zIndex]}`
      );
      continue;
    }

    const id = parts[idIndex];
    // const otherTags = parts.reduce((acc, part, index) => {
    //   acc[headers[index]] = part;
    //   return acc;
    // }, {} as Record<string, string>);
    const item: Point3D = {
      id: id,
      // originalId: `prot_${i.toString().padStart(6, '0')}`,
      x,
      y,
      z,
      // ...otherTags,
    };
    parts.forEach((part, index) => {
      if (
        index === idIndex ||
        index === xIndex ||
        index === yIndex ||
        index === zIndex
      )
        return;
      item[headers[index]] = part;
    });

    groupKeysList.forEach((key) => {
      const dataObj = groupData[key];
      const groupNameRaw = parts[dataObj.index]?.trim() || "";
      const groupName = isValid(groupNameRaw) ? groupNameRaw : "Unknown";
      if (dataObj.dataCount[groupName]) {
        dataObj.dataCount[groupName].count++;
      } else {
        dataObj.dataCount[groupName] = { count: 1, color: "" };
      }
      item[`:${key}Name`] = groupName;
      if (groupName === "Unknown") {
        item[`:${key}Color`] = UNKNOWN_COLOR;
        item[`:Right:Color`] = UNKNOWN_COLOR;
      } else {
        item[`:${key}Color`] = OTHER_COLOR;
        item[`:Right:Color`] = OTHER_COLOR;
      }
    });

    rawPoints.push(item);
  }

  groupKeysList.forEach((key) => {
    const dataObj = groupData[key];
    const sortedDataCount = Object.entries(dataObj.dataCount)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 50);
    sortedDataCount.forEach(([name, { count }], index) => {
      dataObj.dataCount[name].color =
        BRIGHT_COLORS[index % BRIGHT_COLORS.length];
      if (name === "Unknown") {
        dataObj.dataCount[name].color = UNKNOWN_COLOR;
      }
      dataObj.dataList.push({
        name,
        color: dataObj.dataCount[name].color,
        count,
      });
    });
  });

  rawPoints.forEach((p) => {
    groupKeysList.forEach((key) => {
      const dataObj = groupData[key];
      if (dataObj.dataCount[p[`:${key}Name`] as string]) {
        p[`:${key}Color`] = dataObj.dataCount[p[`:${key}Name`] as string].color;
      }
    });
  });

  // --- Normalization ---
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  rawPoints.forEach((p) => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.z < minZ) minZ = p.z;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
    if (p.z > maxZ) maxZ = p.z;
  });

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const centerZ = (minZ + maxZ) / 2;
  const maxDim = Math.max(maxX - minX, maxY - minY, maxZ - minZ);
  const scale = maxDim > 0 ? 100 / maxDim : 1;
  const points: Point3D[] = rawPoints.map((p, i) => {
    return {
      ...p,
      x: (p.x - centerX) * scale,
      y: (p.y - centerY) * scale,
      z: (p.z - centerZ) * scale,
    };
  });
  return { points, groupData };
};

export const generateSecondGroup = (
  points: Point3D[],
  leftGroupKey: string,
  leftSelectedKey: string,
  rightGroupKey: string
): {
  dataList: DataSet[];
  groupData: { [k: string]: { count: number; color: string } };
} => {
  const dataList: DataSet[] = [];
  const groupData: { [k: string]: { count: number; color: string } } = {};
  points.forEach((p) => {
    if (p[`:${leftGroupKey}Name`] === leftSelectedKey) {
      const groupName = p[`:${rightGroupKey}Name`] as string;
      if (groupData[groupName]) {
        groupData[groupName].count++;
      } else {
        groupData[groupName] = { count: 1, color: "" };
      }
    }
  });
  const sortedGroupData = Object.entries(groupData)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 50);
  sortedGroupData.forEach(([name, { count }], index) => {
    groupData[name].color = BRIGHT_COLORS[index % BRIGHT_COLORS.length];
    dataList.push({ name, color: groupData[name].color, count });
  });
  return { dataList, groupData };
};
